import type { CSSProperties, ReactNode, SVGProps } from "react";

// Audiebant Platform — icon set (lucide-style line icons, 1.6 stroke).
// Ported verbatim from the design source (jowcey/project/icons.jsx).

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, "stroke" | "fill"> {
  size?: number;
  fill?: string;
  stroke?: string;
  sw?: number;
  d?: string;
  children?: ReactNode;
  style?: CSSProperties;
}

const Ico = ({ d, size = 18, fill = "none", stroke = "currentColor", sw = 1.6, children, style, ...rest }: IconProps) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill={fill}
    stroke={stroke}
    strokeWidth={sw}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={style}
    {...rest}
  >
    {d ? <path d={d} /> : children}
  </svg>
);

type IconComponent = (p: Omit<IconProps, "children" | "d">) => JSX.Element;

// Note: do NOT type this as `Record<string, IconComponent>` — that
// widens `keyof typeof Icons` to `string`, which means downstream
// code like `icon: keyof typeof Icons` accepts ANY string. A typo
// (or a name that exists in the design source but was never ported
// here) compiles fine and crashes at runtime with React #130 —
// because `Icons[badName]` is undefined and `<undefined />` is the
// invariant violation. Leaving the literal object inferred keeps
// the key union honest. The IconComponent type is still applied
// via `satisfies` so we don't lose the value-type check.
export const Icons = {
  Dashboard: (p) => <Ico {...p}><rect x="3" y="3" width="7" height="9" rx="1.5"/><rect x="14" y="3" width="7" height="5" rx="1.5"/><rect x="14" y="12" width="7" height="9" rx="1.5"/><rect x="3" y="16" width="7" height="5" rx="1.5"/></Ico>,
  Ticket: (p) => <Ico {...p}><path d="M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4z"/><path d="M13 6v12" strokeDasharray="2 2"/></Ico>,
  Drop: (p) => <Ico {...p}><path d="M12 3s6 6.5 6 11a6 6 0 1 1-12 0c0-4.5 6-11 6-11z"/></Ico>,
  Truck: (p) => <Ico {...p}><path d="M3 7h11v9H3z"/><path d="M14 10h4l3 3v3h-7"/><circle cx="7" cy="18" r="2"/><circle cx="17" cy="18" r="2"/></Ico>,
  Form: (p) => <Ico {...p}><rect x="4" y="3" width="16" height="18" rx="2"/><path d="M8 8h8M8 12h8M8 16h5"/></Ico>,
  Shield: (p) => <Ico {...p}><path d="M12 3l8 3v6c0 5-3.5 8.5-8 9-4.5-.5-8-4-8-9V6z"/></Ico>,
  Search: (p) => <Ico {...p}><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></Ico>,
  Bell: (p) => <Ico {...p}><path d="M6 8a6 6 0 1 1 12 0c0 5 2 6 2 6H4s2-1 2-6"/><path d="M10 19a2 2 0 0 0 4 0"/></Ico>,
  Sun: (p) => <Ico {...p}><circle cx="12" cy="12" r="4"/><path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.5 5.5l1.4 1.4M17.1 17.1l1.4 1.4M5.5 18.5l1.4-1.4M17.1 6.9l1.4-1.4"/></Ico>,
  Moon: (p) => <Ico {...p}><path d="M20 14a8 8 0 1 1-10-10 7 7 0 0 0 10 10z"/></Ico>,
  Plus: (p) => <Ico {...p}><path d="M12 5v14M5 12h14"/></Ico>,
  Filter: (p) => <Ico {...p}><path d="M3 5h18l-7 9v6l-4-2v-4z"/></Ico>,
  Sort: (p) => <Ico {...p}><path d="M7 4v16M3 8l4-4 4 4M17 20V4M13 16l4 4 4-4"/></Ico>,
  ChevDown: (p) => <Ico {...p}><path d="m6 9 6 6 6-6"/></Ico>,
  ChevRight: (p) => <Ico {...p}><path d="m9 6 6 6-6 6"/></Ico>,
  ChevLeft: (p) => <Ico {...p}><path d="m15 6-6 6 6 6"/></Ico>,
  X: (p) => <Ico {...p}><path d="M6 6l12 12M18 6 6 18"/></Ico>,
  Check: (p) => <Ico {...p}><path d="m4 12 5 5L20 6"/></Ico>,
  AlertTri: (p) => <Ico {...p}><path d="M12 4 2 20h20z"/><path d="M12 10v4M12 17v.01"/></Ico>,
  Info: (p) => <Ico {...p}><circle cx="12" cy="12" r="9"/><path d="M12 8v.01M11 12h1v5h1"/></Ico>,
  Pin: (p) => <Ico {...p}><path d="M12 21v-7M5 9c0-3 3-6 7-6s7 3 7 6c0 3-3 5-7 5S5 12 5 9z"/></Ico>,
  Map: (p) => <Ico {...p}><path d="M9 3 3 6v15l6-3 6 3 6-3V3l-6 3z"/><path d="M9 3v15M15 6v15"/></Ico>,
  Clock: (p) => <Ico {...p}><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></Ico>,
  Upload: (p) => <Ico {...p}><path d="M12 15V3M7 8l5-5 5 5"/><path d="M5 17v2a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-2"/></Ico>,
  File: (p) => <Ico {...p}><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/><path d="M14 3v6h6"/></Ico>,
  Settings: (p) => <Ico {...p}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></Ico>,
  Help: (p) => <Ico {...p}><circle cx="12" cy="12" r="9"/><path d="M9.5 9a2.5 2.5 0 1 1 3.5 2.3c-.8.4-1 1-1 1.7M12 17v.01"/></Ico>,
  Logo: (p) => <Ico {...p}><path d="M4 18 12 4l8 14"/><path d="M8 14h8"/></Ico>,
  Paperclip: (p) => <Ico {...p}><path d="M21 11.5 12 20a5 5 0 0 1-7-7l9-9a3.5 3.5 0 0 1 5 5l-9 9a2 2 0 0 1-3-3l8.5-8.5"/></Ico>,
  Lock: (p) => <Ico {...p}><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></Ico>,
  Pencil: (p) => <Ico {...p}><path d="M4 20h4l10-10-4-4L4 16z"/><path d="m13 7 4 4"/></Ico>,
  Trash: (p) => <Ico {...p}><path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></Ico>,
  More: (p) => <Ico {...p}><circle cx="5" cy="12" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="19" cy="12" r="1.5"/></Ico>,
  Building: (p) => <Ico {...p}><rect x="4" y="3" width="16" height="18"/><path d="M9 7h2M13 7h2M9 11h2M13 11h2M9 15h2M13 15h2"/></Ico>,
  Send: (p) => <Ico {...p}><path d="M22 2 11 13M22 2l-7 20-4-9-9-4z"/></Ico>,
  ArrowR: (p) => <Ico {...p}><path d="M5 12h14M13 5l7 7-7 7"/></Ico>,
  Play: (p) => <Ico {...p}><path d="M6 4v16l14-8z"/></Ico>,
  Pause: (p) => <Ico {...p}><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></Ico>,
  Route: (p) => <Ico {...p}><circle cx="6" cy="19" r="3"/><circle cx="18" cy="5" r="3"/><path d="M6 16V8a3 3 0 0 1 3-3h6a3 3 0 0 1 3 3v8"/></Ico>,
  Layers: (p) => <Ico {...p}><path d="M12 3 2 8l10 5 10-5z"/><path d="M2 13l10 5 10-5M2 18l10 5 10-5"/></Ico>,
  Sliders: (p) => <Ico {...p}><path d="M4 6h12M20 6h0M4 12h6M14 12h6M4 18h10M18 18h2"/><circle cx="17" cy="6" r="2"/><circle cx="11" cy="12" r="2"/><circle cx="15" cy="18" r="2"/></Ico>,
  Grid: (p) => <Ico {...p}><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></Ico>,
  List: (p) => <Ico {...p}><path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"/></Ico>,
  Download: (p) => <Ico {...p}><path d="M12 3v12M7 11l5 5 5-5"/><path d="M5 19h14"/></Ico>,
  Calendar: (p) => <Ico {...p}><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 9h18M8 3v4M16 3v4"/></Ico>,
  Wrench: (p) => <Ico {...p}><path d="M14 7a4 4 0 0 0 5 5l3 3-5 5-3-3a4 4 0 0 0-5-5l-5-5 3-3z"/></Ico>,
  Bolt: (p) => <Ico {...p}><path d="M13 3 4 14h7l-1 7 9-11h-7z"/></Ico>,
  Pricing: (p) => <Ico {...p}><path d="M3 3h7l11 11-7 7L3 10z"/><circle cx="8" cy="8" r="1.5"/></Ico>,
  Sparkle: (p) => <Ico {...p}><path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M5.6 18.4l2.8-2.8M15.6 8.4l2.8-2.8"/></Ico>,
  TrendUp: (p) => <Ico {...p}><path d="M3 17 9 11l4 4 8-8"/><path d="M14 5h7v7"/></Ico>,
  Onboard: (p) => <Ico {...p}><path d="M12 3v18M3 12h18"/><circle cx="12" cy="12" r="9" strokeDasharray="3 3"/></Ico>,
  Eye:     (p) => <Ico {...p}><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/></Ico>,
  Plug:    (p) => <Ico {...p}><path d="M9 4v6M15 4v6M7 10h10v3a5 5 0 0 1-10 0z"/><path d="M12 18v3"/></Ico>,
  Mail:    (p) => <Ico {...p}><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></Ico>,
  ChevronRight: (p) => <Ico {...p}><path d="m9 6 6 6-6 6"/></Ico>,
  ChevronLeft:  (p) => <Ico {...p}><path d="m15 6-6 6 6 6"/></Ico>,
  ExternalLink: (p) => <Ico {...p}><path d="M14 4h6v6"/><path d="M21 3l-9 9"/><path d="M5 5h6M5 5v14h14v-6"/></Ico>,
  Save:    (p) => <Ico {...p}><path d="M4 4h12l4 4v12H4z"/><path d="M8 4v6h8V4M8 20v-6h8v6"/></Ico>,
  User:    (p) => <Ico {...p}><circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/></Ico>,
  // Boxes — referenced by the legionella sidebar's "Assets" nav entry.
  // Missing here was the root cause of the always-crashing legionella
  // post-auth render (React #130 — Icons["Boxes"] was undefined).
  Boxes:   (p) => <Ico {...p}><rect x="3" y="3" width="8" height="8" rx="1"/><rect x="13" y="3" width="8" height="8" rx="1"/><rect x="3" y="13" width="8" height="8" rx="1"/><rect x="13" y="13" width="8" height="8" rx="1"/></Ico>,
} satisfies Record<string, IconComponent>;

/** Type-safe icon name — extends to a literal union of every defined key. */
export type IconName = keyof typeof Icons;

/**
 * Safe icon lookup that returns a transparent placeholder when the
 * requested name is missing, instead of returning `undefined` and
 * crashing React with #130. Static call sites should prefer the
 * literal `Icons.Boxes` form so a typo is a compile error; this is
 * for the cases where the icon name comes from a config object or a
 * server response.
 */
export function getIcon(name: string | null | undefined): IconComponent {
  if (name && name in Icons) {
    return (Icons as Record<string, IconComponent>)[name];
  }
  // Eslint-friendly noop component; renders an empty svg of the
  // requested size so layout doesn't jump. Also logs once in dev so
  // missing icons get noticed instead of silently disappearing.
  if (import.meta.env.DEV && name) {
    // eslint-disable-next-line no-console
    console.warn(`[Icons] unknown icon "${name}" — add it to src/components/Icons.tsx`);
  }
  return (p) => <Ico {...p} d="" />;
}
